import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { createSelector } from 'reselect';
import { fetchUserProfile } from "../../slices/profile/profileSlice";
import { useTranslation } from 'react-i18next';

// Function to generate a color based on name/email
const getAvatarColor = (text) => {
    const colors = ['#4F46E5', '#06B6D4', '#EC4899', '#F59E0B', '#8B5CF6', '#10B981', '#EF4444'];
    let hash = 0;
    if (text) {
        for (let i = 0; i < text.length; i++) {
            hash = text.charCodeAt(i) + ((hash << 5) - hash);
        }
    }
    return colors[Math.abs(hash) % colors.length];
};

// Function to get first character
const getFirstCharacter = (text) => {
    return text && text.length > 0 ? text.charAt(0).toUpperCase() : 'U';
};

const ProfileDropdown = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const selectProfileData = createSelector(
        (state) => state.ProfileData,
        (profileData) => profileData.profile
    );
    
    const profile = useSelector(selectProfileData);
    const [userName, setUserName] = useState(t("User"));
    const [userImage, setUserImage] = useState(null);
    const [avatarBgColor, setAvatarBgColor] = useState("#4F46E5");
    const [firstChar, setFirstChar] = useState("U");

    useEffect(() => {
        // Only fetch profile if we don't already have user data in sessionStorage
        const authUser = sessionStorage.getItem("authUser");
        if (!authUser) {
            dispatch(fetchUserProfile());
        }
    }, [dispatch]);

    useEffect(() => {
        if (sessionStorage.getItem("authUser")) {
            const obj = JSON.parse(sessionStorage.getItem("authUser"));
            const displayName = obj.name || obj.email || t("User");
            setUserName(displayName);
            setFirstChar(getFirstCharacter(displayName));
            setAvatarBgColor(getAvatarColor(displayName));
        }

        if (profile) {
            if (profile.profileImage) {
                setUserImage(profile.profileImage);
            }
            if (profile.name) {
                setUserName(profile.name);
                setFirstChar(getFirstCharacter(profile.name));
                setAvatarBgColor(getAvatarColor(profile.name));
            }
        }
    }, [profile]);

    //Dropdown Toggle
    const [isProfileDropdown, setIsProfileDropdown] = useState(false);
    const toggleProfileDropdown = () => {
        setIsProfileDropdown(!isProfileDropdown);
    };

    return (
        <React.Fragment>
            <Dropdown isOpen={isProfileDropdown} toggle={toggleProfileDropdown} className="header-item topbar-user d-none d-sm-block" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '36px',
                width: '36px',
                margin: '0',
                padding: '0'
            }}>
                <DropdownToggle tag="button" type="button" className="btn" style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '0',
                    margin: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: '36px',
                    width: '36px',
                    borderRadius: '0.5rem'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                    e.currentTarget.style.backgroundColor = 'rgba(91, 127, 237, 0.08)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
                >
                    {userImage ? (
                        <img className="rounded-circle header-profile-user" src={userImage}
                            alt="Header Avatar"
                            style={{
                                width: '36px',
                                height: '36px',
                                objectFit: 'cover'
                            }} />
                    ) : (
                        <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                            style={{
                                backgroundColor: avatarBgColor,
                                width: '36px',
                                height: '36px',
                                fontSize: '15px',
                                fontWeight: '700',
                                flexShrink: 0
                            }}
                        >
                            {firstChar}
                        </div>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-end">
                    <h6 className="dropdown-header">{t('Welcome')} {userName}!</h6>
                    <DropdownItem className='p-0'>
                        <Link to="/profile" className="dropdown-item">
                            <i className="mdi mdi-account-circle text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">{t('Profile')}</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to="/team" className="dropdown-item">
                            <i className="mdi mdi-account-group text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">{t('Team')}</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to="/onboarding" className="dropdown-item">
                            <i className="mdi mdi-compass text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">{t('Onboarding')}</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to="/settings" className="dropdown-item">
                            <i className="mdi mdi-cog text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">{t('Settings')}</span>
                        </Link>
                    </DropdownItem>
                    <div className="dropdown-divider"></div>
                    <DropdownItem className='p-0'>
                        <Link to="/plans" className="dropdown-item">
                            <i className="mdi mdi-crown text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">{t('Plans')}</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to="/billing" className="dropdown-item">
                            <i className="mdi mdi-credit-card-outline text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">{t('Billing')}</span>
                        </Link>
                    </DropdownItem>
                    <DropdownItem className='p-0'>
                        <Link to="/invoices" className="dropdown-item">
                            <i className="mdi mdi-file-document-outline text-muted fs-16 align-middle me-1"></i>
                            <span className="align-middle">{t('Invoices')}</span>
                        </Link>
                    </DropdownItem>
                    <div className="dropdown-divider"></div>
                    <DropdownItem className='p-0'>
                        <Link to="/logout" className="dropdown-item">
                            <i
                                className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i> <span
                                    className="align-middle" data-key="t-logout">{t('Logout')}</span>
                        </Link>
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        </React.Fragment>
    );
};

export default ProfileDropdown;